/**
 * 車輛服務
 * 統一處理車輛相關 API，支援 Mock/真實 API 切換
 */

import { config } from './config'
import apiClient, { ApiBusinessError } from './apiClient'
import {
  mockGetCompanyVehicles,
  mockGetVehicleHistory,
  mockGetVehicleState,
  mockUpdateVehicleState,
} from '@/data/mockData'
import type { Vehicle, VehicleHistory, LastHandover, MockVehicle, MockVehicleHistory, CurrentDuty, HandoverValidation } from '@/types'

/**
 * 取得公司車輛列表
 * @param companyName 公司名稱（Mock 模式使用）
 * @param lineIdToken LINE ID Token（真實 API 模式使用，但 apiClient 會自動注入）
 */
export async function getCompanyVehicles(companyName: string, _lineIdToken?: string): Promise<Vehicle[]> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: getCompanyVehicles')
    // Mock 回傳的是 MockVehicle，需要轉換為 Vehicle
    const mockVehicles = await mockGetCompanyVehicles(companyName)
    return mockVehicles.map(convertMockVehicleToVehicle)
  }

  console.log('[VehicleService] 呼叫 API: getCompanyVehicles')
  try {
    // apiClient.get 會自動處理 ApiResponse wrapper，直接取得 data
    const vehicles = await apiClient.get<Vehicle[]>('/line/vehicles')
    return vehicles || []
  } catch (error) {
    console.error('[VehicleService] getCompanyVehicles 失敗:', error)
    return []
  }
}

/**
 * 取得司機車輛使用歷史
 * @param driverId 司機編號（Mock 模式使用）
 * @param lineIdToken LINE ID Token（真實 API 模式使用，但 apiClient 會自動注入）
 */
export async function getVehicleHistory(driverId: string, _lineIdToken?: string): Promise<VehicleHistory[]> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: getVehicleHistory')
    // Mock 回傳的是 { history: MockVehicleHistory[] }，需要轉換
    const mockResponse = await mockGetVehicleHistory(driverId)
    return mockResponse.history.map(convertMockHistoryToHistory)
  }

  console.log('[VehicleService] 呼叫 API: getVehicleHistory')
  try {
    // apiClient.get 會自動處理 ApiResponse wrapper，直接取得 data（陣列）
    const history = await apiClient.get<VehicleHistory[]>('/line/drivers/me/vehicle-history')
    return history || []
  } catch (error) {
    console.error('[VehicleService] getVehicleHistory 失敗:', error)
    return []
  }
}

/**
 * 取得車輛交班資訊
 * @param vehicleId 車輛 ID（實際 API 使用數字）
 * @returns 上一位司機的交班資訊（油量、里程等）
 */
export async function getLastHandover(vehicleId: string | number): Promise<LastHandover | null> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: getLastHandover')
    const mockState = await mockGetVehicleState(String(vehicleId))
    if (!mockState) return null
    // 將 Mock VehicleState 轉換為 LastHandover
    return {
      vehicleId: parseInt(mockState.vehicleId.replace('V', ''), 10) || 0,
      vehicleNo: mockState.vehicleId,
      lastDriver: {
        driverId: mockState.lastUpdatedBy,
        driverName: mockState.lastUpdatedByName,
      },
      handoverTime: mockState.lastUpdatedAt,
      fuelLevel: mockState.fuelLevel,
      mileage: mockState.mileage,
      remark: null,
    }
  }

  console.log('[VehicleService] 呼叫 API: getLastHandover')
  try {
    const handover = await apiClient.get<LastHandover>(`/line/vehicles/${vehicleId}/last-handover`)
    return handover
  } catch (error) {
    console.error('[VehicleService] getLastHandover 失敗:', error)
    return null
  }
}

/**
 * 接班（上工）
 * @param vehicleId 車輛 ID
 * @param vehicleNo 車牌號碼
 * @param fuelLevel 油量
 * @param mileage 里程數
 * @param driverId 司機編號（Mock 模式使用）
 */
export async function startDuty(
  vehicleId: string | number,
  vehicleNo: string,
  fuelLevel: number,
  mileage: number,
  driverId: string
): Promise<{ success: boolean; message: string }> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: startDuty')
    return mockUpdateVehicleState(String(vehicleId), fuelLevel, mileage, driverId)
  }

  console.log('[VehicleService] 呼叫 API: startDuty')
  try {
    await apiClient.post('/line/attendances/start-duty', {
      vehicleId: String(vehicleId),
      vehicleNo,
      fuelLevel,
      mileage,
    })
    return { success: true, message: '接班成功' }
  } catch (error) {
    console.error('[VehicleService] startDuty 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    const errorMessage = error instanceof Error ? error.message : '接班失敗'
    return { success: false, message: errorMessage }
  }
}

/**
 * 交班 API 回應
 */
interface HandoverResponse {
  message: string
  vehicleId: string
  reportDate: string  // 班別日期 (yyyy-MM-dd)
}

/**
 * 車輛交班
 * @param vehicleId 車輛 ID
 * @param fuelLevel 油量
 * @param mileage 里程數
 * @param driverId 司機編號（Mock 模式使用）
 * @param fuelAdded 本日加油量（公升，預設 0）
 */
export async function updateVehicleState(
  vehicleId: string | number,
  fuelLevel: number,
  mileage: number,
  driverId: string,
  fuelAdded: number = 0
): Promise<{ success: boolean; message: string; reportDate?: string; statusCode?: string }> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: updateVehicleState')
    const result = mockUpdateVehicleState(String(vehicleId), fuelLevel, mileage, driverId)
    // Mock 模式回傳今天日期
    const today = new Date().toISOString().split('T')[0]
    return { ...result, reportDate: today }
  }

  console.log('[VehicleService] 呼叫 API: handover')
  try {
    const response = await apiClient.put<HandoverResponse>(`/line/vehicles/${vehicleId}/handover`, {
      fuelLevel,
      mileage,
      fuelAdded,
    })
    return { success: true, message: '交班成功', reportDate: response.reportDate }
  } catch (error) {
    console.error('[VehicleService] handover 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc, statusCode: error.code }
    }
    const errorMessage = error instanceof Error ? error.message : '交班失敗'
    return { success: false, message: errorMessage }
  }
}

/**
 * 取得當前出勤資訊
 * 用於任務列表頁面載入當前工作中的車輛資訊
 */
export async function getCurrentDuty(): Promise<CurrentDuty | null> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: getCurrentDuty')
    // Mock 模式回傳 null（沒有出勤記錄）
    return null
  }

  console.log('[VehicleService] 呼叫 API: getCurrentDuty')
  try {
    const duty = await apiClient.get<CurrentDuty>('/line/attendances/current')
    return duty
  } catch (error) {
    console.error('[VehicleService] getCurrentDuty 失敗:', error)
    return null
  }
}

/**
 * 交班前檢核 (Rule-10)
 * 檢核司機是否有未完成的任務阻擋交班
 * @returns 檢核結果（包含阻擋的任務清單）
 */
export async function validateHandover(): Promise<HandoverValidation> {
  if (config.useMock) {
    console.log('[VehicleService] 使用 Mock: validateHandover')
    // Mock 模式回傳可交班
    return {
      canHandover: true,
      summary: null,
      message: null,
      blockingTasks: [],
    }
  }

  console.log('[VehicleService] 呼叫 API: validateHandover')
  try {
    const result = await apiClient.get<HandoverValidation>('/line/drivers/me/handover/validate')
    return result
  } catch (error) {
    console.error('[VehicleService] validateHandover 失敗:', error)
    // 發生錯誤時，預設為可交班（避免阻擋使用者）
    return {
      canHandover: true,
      summary: null,
      message: null,
      blockingTasks: [],
    }
  }
}

/**
 * 排序車輛列表
 * 1. 最近一次使用的車輛排第一
 * 2. 其餘有使用記錄的車輛按使用次數降序
 * 3. 無使用記錄的車輛按車號字母序
 * @param vehicles 車輛列表
 * @param history 使用歷史
 */
export function getSortedVehicles(vehicles: Vehicle[], history: VehicleHistory[]): Vehicle[] {
  const historyMap = new Map(history.map((h) => [h.vehicleId, h]))

  // 分類車輛：有使用記錄 vs 無使用記錄
  const usedVehicles: { vehicle: Vehicle; lastUsedAt: string; useCount: number }[] = []
  const unusedVehicles: Vehicle[] = []

  vehicles.forEach((vehicle) => {
    const hist = historyMap.get(vehicle.vehicleId)
    if (hist) {
      usedVehicles.push({ vehicle, lastUsedAt: hist.lastUsedAt, useCount: hist.usageCount || 0 })
    } else {
      unusedVehicles.push(vehicle)
    }
  })

  // 找出最近一次使用的車輛（排第一位）
  const sorted = [...usedVehicles].sort(
    (a, b) => new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
  )
  const mostRecent = sorted[0] || null
  const restUsed = sorted.slice(1).sort((a, b) => b.useCount - a.useCount)

  // 無使用記錄的車輛按車號排序
  unusedVehicles.sort((a, b) => a.vehicleNo.localeCompare(b.vehicleNo))

  return [
    ...(mostRecent ? [mostRecent.vehicle] : []),
    ...restUsed.map((u) => u.vehicle),
    ...unusedVehicles,
  ]
}

// ========== Mock 資料轉換函數 ==========

/**
 * 將 Mock Vehicle 轉換為實際 Vehicle 型別
 */
function convertMockVehicleToVehicle(mock: MockVehicle): Vehicle {
  const statusMap: Record<string, 'AVAILABLE' | 'IN_USE' | 'MAINTENANCE'> = {
    available: 'AVAILABLE',
    in_use: 'IN_USE',
    maintenance: 'MAINTENANCE',
  }
  const statusNameMap: Record<string, string> = {
    available: '可用',
    in_use: '使用中',
    maintenance: '維修中',
  }

  return {
    vehicleId: parseInt(mock.vehicleId.replace('V', ''), 10) || 0,
    vehicleNo: mock.vehicleNumber,
    vehicleType: mock.vehicleType,
    vehicleTypeName: mock.vehicleType,
    status: statusMap[mock.status] || 'AVAILABLE',
    statusName: statusNameMap[mock.status] || '可用',
    currentDriverId: null,
    currentDriverName: null,
  }
}

/**
 * 將 Mock VehicleHistory 轉換為實際 VehicleHistory 型別
 */
function convertMockHistoryToHistory(mock: MockVehicleHistory): VehicleHistory {
  return {
    vehicleId: parseInt(mock.vehicleId.replace('V', ''), 10) || 0,
    vehicleNo: mock.vehicleId, // Mock 沒有 vehicleNo，暫用 vehicleId
    usageCount: mock.useCount,
    lastUsedAt: mock.lastUsedAt,
  }
}
